import type { Entity } from '@backstage/catalog-model';
import { ErrorPanel, Progress } from '@backstage/core-components';
import { catalogApiRef, EntityRefLink } from '@backstage/plugin-catalog-react';
import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
import { identityApiRef, useApi } from '@backstage/frontend-plugin-api';
import useAsync from 'react-use/esm/useAsync';

export function MyEntitiesWidgetContent() {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const { value, loading, error } = useAsync(async (): Promise<Entity[]> => {
    const identidad = await identityApi.getBackstageIdentity();
    const referencias = identidad.ownershipEntityRefs;

    if (referencias.length === 0) {
      return [];
    }

    const respuesta = await catalogApi.getEntities({
      filter: {
        kind: ['Component', 'API', 'System'],
        'relations.ownedBy': referencias,
      },
    });
    return respuesta.items;
  }, [identityApi, catalogApi]);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ErrorPanel error={error} />;
  }

  if (!value || value.length === 0) {
    return (
      <Typography variant="body2">
        No eres owner de ninguna entidad. Revisa tu grupo en el SSO.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableBody>
        {value.map(entidad => (
          <TableRow
            key={`${entidad.kind}:${entidad.metadata.namespace}/${entidad.metadata.name}`}
          >
            <TableCell>
              <EntityRefLink entityRef={entidad} />
            </TableCell>
            <TableCell>
              {entidad.spec?.type && (
                <Chip size="small" label={String(entidad.spec.type)} />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export const myEntitiesWidget = HomePageWidgetBlueprint.make({
  name: 'my-entities',
  params: {
    name: 'my-entities',
    title: 'Mis entidades',
    components: async () => ({ Content: MyEntitiesWidgetContent }),
  },
});
